/**
 * Simple, consistent product copy for marketing + dashboard hints.
 * Edit here to change wording site-wide.
 */

export const SITE_SUMMARY =
  "Manage360 is your AI-powered life admin hub—Screen AI, Media AI, Audio AI, Video AI, Exam AI, DayLife AI, Portfolio AI, Project AI, Todos AI, Daily Chore AI, Transfer AI travel board, org tree, notes, files, calendar, and group bills in one login.";

/** Short line for the strip on login, FAQ, contact, etc. (not the home page hero). */
export const SITE_STRIP_LINE =
  "One dashboard with AI for screen, media, audio, video, money, projects, todos, and routines—plus a traveller board, notes, calendar, and splits.";

/** Home hero headline + supporting lines (Hero.js imports these). */
export const HOME_HERO = {
  badge: "Start free · full dashboard · AI when you want it",
  headlineLine1: "Your personal AI assistant",
  headlineLine2: "for a seamless everyday life",
  line1:
    "Screen, media, audio, and video AI, voice on money & tasks, a traveller board—plus notes, calendar, and shared bills in one calm workspace.",
  line2:
    "Use AI Usage Credits for smart capture and voice commands, or work with taps only. You choose.",
};

/** Home “What makes us different” — unique selling points. */
export const HOME_UNIQUE_FEATURES = {
  badge: "Why Manage360 is different",
  heading: "AI where it actually saves you time",
  sub:
    "Most apps make you pick one job. Manage360 combines capture AI, voice-driven tabs, and everyday planning tools behind a single sidebar.",
  items: [
    {
      title: "Screen AI",
      description:
        "Share a lesson or meeting tab—screenshots and audio become searchable summaries you can ask about later.",
      accent: "cyan",
    },
    {
      title: "Media AI",
      description:
        "Upload slides, photos, or video; AI explains each image or walks through video in ~30 second segments.",
      accent: "purple",
    },
    {
      title: "Audio AI",
      description:
        "Record voice notes or upload audio files—multiple takes in one box—then get transcripts, summaries, and answers to your questions.",
      accent: "cyan",
    },
    {
      title: "Video AI",
      description:
        "Record or upload videos and AI builds a moment-by-moment timeline—then ask 'is this person seen?' or 'which number plates appear?' with timestamped answers.",
      accent: "purple",
    },
    {
      title: "Transfer AI",
      description:
        "A community travel board: post your route and date, search who's travelling ('del' → Delhi works), and chat to request they carry something along.",
      accent: "orange",
    },
    {
      title: "Portfolio AI",
      description:
        "Track income and spending in folders, then log or query totals with voice instead of retyping rupee lines.",
      accent: "teal",
    },
    {
      title: "Project AI",
      description:
        "Kanban boards with due dates—move cards and update tasks by voice when credits are available.",
      accent: "blue",
    },
    {
      title: "Todos AI",
      description:
        "Nested checklists for today; add, complete, and reorganize subtasks hands-free or with clicks.",
      accent: "green",
    },
    {
      title: "Daily Chore AI",
      description:
        "Recurring home or work routines on a calendar with SMS/email reminders—and voice to check what’s left today.",
      accent: "purple",
    },
    {
      title: "Exam AI",
      description:
        "Pick any real exam—Exam AI builds the full syllabus tree and tutors you topic by topic with fact-checked answers.",
      accent: "pink",
    },
    {
      title: "3D org tree",
      description:
        "Orbit a visual org map with per-node projects and todos; local voice navigation needs no AI credits.",
      accent: "orange",
    },
    {
      title: "Free core workspace",
      description:
        "Notes, files, calendar, and Splitwise-style groups stay free. AI Usage Credits are optional prepaid top-ups.",
      accent: "teal",
    },
  ],
};

/** Home “Features” section title + subtitle (keep short). */
export const FEATURES_SECTION = {
  badge: "Included in your free workspace",
  heading: "Every tool in one sidebar",
  sub:
    "Open Screen AI, Media AI, Audio AI, Video AI, Transfer AI, Exam AI, DayLife AI, Portfolio AI, Project AI, Todos AI, Daily Chore AI, notes, files, org tree, calendar, and group splits—same login.",
};

/**
 * For in-app tab banners: what this screen is for + how it helps.
 * Keys match the first segment after `/dashboard/`.
 */
export const DASHBOARD_TAB_GUIDES = {
  profile: {
    title: "Profile",
    intro:
      "Identity + contacts Manage360 trusts for OTP sign-in and outbound reminders—not a billing console.",
    helps: [
      "Keep phone/email true or login codes and alerts fail to reach you.",
      "Adjust name/details anytime; carriers still dictate SMS/WhatsApp quirks.",
      "AI Usage Credits top-ups start from prompts when you use AI features—those flows sit outside this form.",
    ],
  },
  "media-analyze": {
    title: "Media AI",
    intro:
      "Upload images, a folder of pictures, or a video—AI summarizes each image or explains the video in time segments.",
    helps: [
      "Uses AI Usage Credits from Profile (same balance as Screen AI).",
      "Images: one AI summary per file. Videos: segments about every 30 seconds.",
      "Past analyses stay in the sidebar; open any job to read all summaries again.",
    ],
  },
  "exam-ai": {
    title: "Exam AI",
    intro:
      "Pick an exam and Exam AI builds its full syllabus as subjects and sub-topics, then lets you study by chatting with a tutor in any topic.",
    helps: [
      "Add any real exam (e.g. UPSC, JEE, NEET)—random names are blocked by AI validation.",
      "Subjects (categories) and sub-topics auto-generate; switch exams to swap the whole tree.",
      "Chat inside any subject or topic—answers are fact-checked by a second AI pass. Uses AI Usage Credits.",
      "Current Affairs tab: pick a date for The Hindu + Indian Express articles, then practice MCQs drawn strictly from them with score analytics.",
    ],
  },
  "day-life": {
    title: "DayLife AI",
    intro:
      "A daily journal with an AI coach: each day note what you did well and what you could have done better, then ask AI to spot patterns and tell you how to improve.",
    helps: [
      "Write only for today—past days are read-only and future days are locked.",
      "Analyse any window (last 7 days, 1 month, current month, 1 year, or a custom range) for honest, specific advice.",
      "Every analysis is saved and feeds later conversations, so the coaching stays consistent over time. Analysis uses AI Usage Credits.",
    ],
  },
  "screen-listen": {
    title: "Screen AI",
    intro:
      "Share a tab or app, capture screenshots and audio, and get a saved AI summary in your history. Uses AI Usage Credits from your profile.",
    helps: [
      "You need AI Usage Credits in Profile before sharing screen—top up if the balance is zero.",
      "Set screenshot interval (e.g. every 10s)—we explain maps, slides, and on-screen text.",
      "Past sessions stay in the sidebar; ask about history without sharing your screen again.",
    ],
  },
  notes: {
    title: "Notes",
    intro:
      "Folders plus search—save links or long prose next to chores, files, and money tabs.",
    helps: [
      "Spin up folders, drop notes inside, rename/delete like a mini wiki.",
      "Search box scans titles/content fast so bookmarks don’t evaporate.",
      "Share folders in-app via collaboration when you invite people (purple share control).",
    ],
  },
  files: {
    title: "Files",
    intro:
      "Hosted uploads grouped like Notes—central PDF/asset shelf inside the dashboard.",
    helps: [
      "Choose folder, upload, download, purge—everything lists in-place.",
      "Pair with portfolios/projects mentally: receipts beside the work that generated them.",
      "Treat it as structured storage, not infinite cold backup.",
    ],
  },
  portfolio: {
    title: "Portfolio AI",
    intro:
      "Track money across folders—like a personal finance workspace—not a regulated bank. Voice and AI summaries use AI Usage Credits.",
    helps: [
      "See totals per folder and drill into transactions you entered.",
      "Log or ask about spending with voice commands (prepaid AI Usage Credits).",
      "Accuracy depends on what you typed; summaries help planning—not tax/legal advice.",
    ],
  },
  project: {
    title: "Project AI",
    intro:
      "Kanban lanes with drag cards, due dates, and nested sub-cards—update work by voice when you have AI Usage Credits.",
    helps: [
      "Move rows across stages (ideas → shipped) visually—dates keep streams honest.",
      "Break work infinitely for epics yet keep one board authoritative.",
      "Voice edits mimic clicks but require AI Usage Credits; otherwise drag/context menus suffice.",
    ],
  },
  "org-tree": {
    title: "Org tree",
    intro:
      "A 3D tree you can orbit: org → teams → people as spheres on wood-like branches, with per-node work summaries, todos, and local voice commands.",
    helps: [
      "Drag to rotate the scene, scroll to zoom; click a node to edit it on the right—Alt+click folds a subtree.",
      "Add depth freely; each node keeps its own checklist. Everything is stored on this device for now.",
      "Voice uses browser speech only (no AI Usage Credits): select by index or name, go back, expand/collapse, add child or todo.",
    ],
  },
  todos: {
    title: "Todos AI",
    intro:
      "Daily checklist trees—check off against “today”, nest subtasks, and use voice when you have AI Usage Credits.",
    helps: [
      "Completed branches can tuck away overnight—the blue Tips link on-page explains hiding rules.",
      "Add subtree via + icons; notes + share controls mirror other modules when exposed.",
      "Voice assistant listens to the same day view—speak/listen needs AI Usage Credits balance.",
    ],
  },
  "daily-chore": {
    title: "Daily Chore AI",
    intro:
      "Recurring chores on a calendar—with reminders and optional voice to add or finish routines (AI Usage Credits).",
    helps: [
      "Pick repeat cadence plus day buckets (server honours India IST splits).",
      "Attach reminders so routines ping the SMS/email/Voice routes you validated.",
      "Reminder delivery still follows Profile + telecom limits you configured.",
    ],
  },
  "calendar-events": {
    title: "Calendar events",
    intro:
      "Google Calendar tether—shows meetings here so Manage360 fires pre-event pings you opted into.",
    helps: [
      "OAuth linking pulls events downward; RSVP edits still happen inside Google Calendar.",
      "Reminder channels obey the same telecom limits as chores—verify endpoints first.",
      "Great companion to todos/chores whenever meetings drive hard deadlines.",
    ],
  },
  splitwise: {
    title: "Splitwise groups",
    intro:
      "Group expense tally—capture who spent what so balances stay fair; cash still moves externally.",
    helps: [
      "Create groups for flats, trips, teams—balances show who owes conceptually—not custodial payouts.",
      "Log splits + notes per charge; reconcile IRL wallets however you agree.",
      "Works beside chores/portfolio mentally: roommate money minus spreadsheet chaos.",
    ],
  },
};

/** Card-sized blurbs on dashboard home (“workspace hub”). */
export const DASHBOARD_HOME_TOOL_BLURBS = [
  {
    tab: "notes",
    title: "Notes",
    line: "Save links and long notes in searchable folders.",
  },
  {
    tab: "files",
    title: "Files",
    line: "Store documents next to everything else.",
  },
  {
    tab: "portfolio",
    title: "Portfolio AI",
    line: "Money folders plus voice logging and queries.",
  },
  {
    tab: "project",
    title: "Project AI",
    line: "Kanban boards with voice-friendly updates.",
  },
  {
    tab: "org-tree",
    title: "Org tree",
    line: "Nested teams, people, and todos with voice drill-down.",
  },
  {
    tab: "todos",
    title: "Todos AI",
    line: "Nested checklist with optional voice control.",
  },
  {
    tab: "daily-chore",
    title: "Daily Chore AI",
    line: "Recurring routines, reminders, and voice.",
  },
  {
    tab: "screen-listen",
    title: "Screen AI",
    line: "Capture tabs; AI summarizes lessons and meetings.",
  },
  {
    tab: "media-analyze",
    title: "Media AI",
    line: "Upload images or video for AI explanations.",
  },
  {
    tab: "voice-analyse",
    title: "Audio AI",
    line: "Record or upload audio; AI transcribes and analyzes it.",
  },
  {
    tab: "video-ai",
    title: "Video AI",
    line: "Timeline of what happens in your videos, with Q&A.",
  },
  {
    tab: "transfer-ai",
    title: "Transfer AI",
    line: "Post and search travellers by route and date, then chat.",
  },
  {
    tab: "exam-ai",
    title: "Exam AI",
    line: "Auto-built syllabus plus a fact-checked tutor.",
  },
  {
    tab: "day-life",
    title: "DayLife AI",
    line: "Daily journal with an AI coach that tracks your growth.",
  },
  {
    tab: "calendar-events",
    title: "Calendar",
    line: "Meeting-linked reminders.",
  },
  {
    tab: "splitwise",
    title: "Groups",
    line: "Shared expenses and balances.",
  },
  {
    tab: "profile",
    title: "Profile",
    line: "Account settings and alert contact info.",
  },
];

/**
 * Full feature catalog for in-app discovery (e.g. the Profile overview).
 * `tab` matches the route under `/dashboard/`; `ai` marks tools that can use
 * prepaid AI Usage Credits. `category` groups them in the UI.
 */
export const FEATURE_CATALOG = [
  {
    tab: "screen-listen",
    title: "Screen AI",
    accent: "cyan",
    ai: true,
    category: "AI features",
    description:
      "Share a tab or app—Screen AI turns screenshots and audio into saved, searchable summaries you can ask about later.",
  },
  {
    tab: "media-analyze",
    title: "Media AI",
    accent: "purple",
    ai: true,
    category: "AI features",
    description:
      "Upload images, a folder of pictures, or a video and get one AI summary per image or a walkthrough every ~30 seconds of video.",
  },
  {
    tab: "voice-analyse",
    title: "Audio AI",
    accent: "cyan",
    ai: true,
    category: "AI features",
    description:
      "Record voice notes or upload audio files—several parts in one recording—then AI transcribes, summarizes, and answers questions about them.",
  },
  {
    tab: "video-ai",
    title: "Video AI",
    accent: "purple",
    ai: true,
    category: "AI features",
    description:
      "Record with your camera or upload videos; AI builds a timestamped timeline of what happens and answers questions like 'is this person seen?'.",
  },
  {
    tab: "transfer-ai",
    title: "Transfer AI",
    accent: "orange",
    ai: false,
    category: "Core workspace",
    description:
      "Post your travel route and date, search other travellers with substring matching, and chat to request they bring something along.",
  },
  {
    tab: "exam-ai",
    title: "Exam AI",
    accent: "pink",
    ai: true,
    category: "AI features",
    description:
      "Pick any real exam to auto-build its syllabus tree, then chat with a fact-checked tutor and practice current-affairs MCQs.",
  },
  {
    tab: "portfolio",
    title: "Portfolio AI",
    accent: "teal",
    ai: true,
    category: "AI features",
    description:
      "Track income and spending across folders, then log entries or ask for totals by voice instead of retyping every line.",
  },
  {
    tab: "project",
    title: "Project AI",
    accent: "blue",
    ai: true,
    category: "AI features",
    description:
      "Kanban boards with due dates and nested sub-cards—drag work across stages or update it hands-free with voice.",
  },
  {
    tab: "todos",
    title: "Todos AI",
    accent: "green",
    ai: true,
    category: "AI features",
    description:
      "Nested checklists for today—add, complete, and reorganize subtasks by tapping or with optional voice control.",
  },
  {
    tab: "daily-chore",
    title: "Daily Chore AI",
    accent: "purple",
    ai: true,
    category: "AI features",
    description:
      "Recurring routines on a calendar with SMS/email reminders, plus voice to check or finish what's left for the day.",
  },
  {
    tab: "day-life",
    title: "DayLife AI",
    accent: "teal",
    ai: true,
    category: "AI features",
    description:
      "Journal what went well and what didn't each day, then ask AI to analyse any period and coach you on how to improve—conversations build on each other over time.",
  },
  {
    tab: "org-tree",
    title: "Org tree",
    accent: "orange",
    ai: false,
    category: "Core workspace",
    description:
      "A 3D org map you can orbit—org → teams → people—with todos per node and free local voice navigation (no credits).",
  },
  {
    tab: "notes",
    title: "Notes",
    accent: "teal",
    ai: false,
    category: "Core workspace",
    description:
      "Save links and long notes in searchable folders and share them in-app when you invite collaborators.",
  },
  {
    tab: "files",
    title: "Files",
    accent: "orange",
    ai: false,
    category: "Core workspace",
    description:
      "Upload, download, and organize PDFs and other documents in folders right beside the rest of your work.",
  },
  {
    tab: "calendar-events",
    title: "Calendar",
    accent: "pink",
    ai: false,
    category: "Core workspace",
    description:
      "Connect Google Calendar to see meetings here and fire the pre-event reminders you opt into.",
  },
  {
    tab: "splitwise",
    title: "Splitwise groups",
    accent: "teal",
    ai: false,
    category: "Core workspace",
    description:
      "Create groups for flats, trips, or teams and track who paid what so balances stay fair.",
  },
];
